import gql from 'graphql-tag'

// Every relation option's label starts with its id, so it's unambiguous
// which record you're looking at even when the descriptive part is sparse.
export function decodeGlobalId(globalId) {
    try {
        const parts = atob(globalId).split(':')
        return parts.length === 2 ? parts[1] : globalId
    } catch (e) {
        return globalId
    }
}

function withIdPrefix(pk, descriptor) {
    return descriptor ? `#${pk} - ${descriptor}` : `#${pk}`
}

// resolveById always uses the model's dedicated single-object query (driver,
// car, incident, rep, carIssue, liabilityClaim, subrogationClaim, group), each
// taking the graphene global id -- exact, unambiguous, and cheap. Never the
// list queries: their free-text searchTerm is an OR'd substring match across
// many fields, so a pk like "1" can match dozens of unrelated records.

// --- Driver ---
const DriverOptionsQuery = gql`
    query DriverRelationOptions($searchTerm: String) {
        allDrivers(searchTerm: $searchTerm, first: 30, orderBy: ["-id"]) {
            edges { node { id pk name tlcLicense } }
        }
    }
`
const DriverByIdQuery = gql`
    query DriverById($id: ID) {
        driver(id: $id) { id pk name tlcLicense }
    }
`
function driverOption(node) {
    return { value: node.id, label: withIdPrefix(node.pk, node.tlcLicense ? `${node.name} (${node.tlcLicense})` : node.name) }
}

// --- Car ---
const CarOptionsQuery = gql`
    query CarRelationOptions($searchTerm: String) {
        allCars(searchTerm: $searchTerm, first: 30, orderBy: ["-id"]) {
            edges { node { id pk year vin dmvPlate carModel { make name } } }
        }
    }
`
const CarByIdQuery = gql`
    query CarById($id: ID) {
        car(gqlId: $id) { id pk year vin dmvPlate carModel { make name } }
    }
`
function carOption(node) {
    const modelLabel = [node.year, node.carModel?.make, node.carModel?.name].filter(Boolean).join(' ')
    const idLabel = node.dmvPlate || node.vin || ''
    return { value: node.id, label: withIdPrefix(node.pk, [modelLabel, idLabel].filter(Boolean).join(' - ')) }
}

// --- User ---
const UserOptionsQuery = gql`
    query UserRelationOptions($searchTerm: String) {
        allReps(searchTerm: $searchTerm, first: 30, orderBy: ["-id"]) {
            edges { node { id pk name username email } }
        }
    }
`
const UserByIdQuery = gql`
    query UserById($id: ID!) {
        rep(id: $id) { id pk name username email }
    }
`
function userOption(node) {
    return { value: node.id, label: withIdPrefix(node.pk, node.name || node.username || node.email) }
}

// --- Incident ---
const IncidentOptionsQuery = gql`
    query IncidentRelationOptions($searchTerm: String) {
        allIncidents(searchTerm: $searchTerm, first: 30, orderBy: ["-dateAdded"]) {
            edges { node { id pk title incidentType } }
        }
    }
`
const IncidentByIdQuery = gql`
    query IncidentById($id: ID!) {
        incident(incidentId: $id) { id pk title incidentType }
    }
`
function incidentOption(node) {
    return { value: node.id, label: withIdPrefix(node.pk, node.title || (node.incidentType ? `${node.incidentType} incident` : '')) }
}

// --- CarIssues ("Damage") ---
const CarIssuesOptionsQuery = gql`
    query CarIssuesRelationOptions($searchTerm: String) {
        allCarIssues(searchTerm: $searchTerm, first: 30, orderBy: ["-id"]) {
            edges { node { id pk issueCategory { category } car { vin dmvPlate } } }
        }
    }
`
const CarIssueByIdQuery = gql`
    query CarIssueById($id: ID!) {
        carIssue(id: $id) { id pk issueCategory { category } car { vin dmvPlate } }
    }
`
function carIssueOption(node) {
    const idLabel = node.car?.dmvPlate || node.car?.vin || ''
    return { value: node.id, label: withIdPrefix(node.pk, [node.issueCategory?.category, idLabel].filter(Boolean).join(' - ')) }
}

// --- LiabilityClaim ---
const LiabilityClaimOptionsQuery = gql`
    query LiabilityClaimRelationOptions($searchTerm: String) {
        allLiabilityClaims(searchTerm: $searchTerm, first: 30, orderBy: ["-id"]) {
            edges { node { id pk externalId incident { title } } }
        }
    }
`
const LiabilityClaimByIdQuery = gql`
    query LiabilityClaimById($id: ID!) {
        liabilityClaim(id: $id) { id pk externalId incident { title } }
    }
`
function liabilityClaimOption(node) {
    return { value: node.id, label: withIdPrefix(node.pk, [node.externalId, node.incident?.title].filter(Boolean).join(' - ')) }
}

// --- SubrogationClaim ---
const SubrogationClaimOptionsQuery = gql`
    query SubrogationClaimRelationOptions($searchTerm: String) {
        allSubrogationClaims(searchTerm: $searchTerm, first: 30, orderBy: ["-id"]) {
            edges { node { id pk externalId incident { title } } }
        }
    }
`
const SubrogationClaimByIdQuery = gql`
    query SubrogationClaimById($id: ID!) {
        subrogationClaim(id: $id) { id pk externalId incident { title } }
    }
`
function subrogationClaimOption(node) {
    return { value: node.id, label: withIdPrefix(node.pk, [node.externalId, node.incident?.title].filter(Boolean).join(' - ')) }
}

// --- Branch ---
// allBranches has no free-text searchTerm; like Group it exposes its model's
// filter_fields, so search by the name icontains filter.
const BranchOptionsQuery = gql`
    query BranchRelationOptions($searchTerm: String) {
        allBranches(name_Icontains: $searchTerm, first: 30, orderBy: ["-id"]) {
            edges { node { id pk name } }
        }
    }
`
const BranchByIdQuery = gql`
    query BranchById($id: ID) {
        branch(id: $id) { id pk name }
    }
`
function branchOption(node) {
    return { value: node.id, label: withIdPrefix(node.pk, node.name) }
}

// --- Group ---
const GroupOptionsQuery = gql`
    query GroupRelationOptions($searchTerm: String) {
        allGroups(name_Icontains: $searchTerm, first: 30) {
            edges { node { id name } }
        }
    }
`
const GroupByIdQuery = gql`
    query GroupById($id: ID!) {
        group(id: $id) { id name }
    }
`
function groupOption(node) {
    return { value: node.id, label: withIdPrefix(decodeGlobalId(node.id), node.name) }
}

// Runs a query and maps a single-node result (`data.<queryFieldPath>`) into an
// option, or null if nothing came back.
function resolveSingular(apolloClient, query, id, fieldPath, mapNode) {
    return apolloClient.query({ query, variables: { id } }).then((result) => {
        const node = result.data?.[fieldPath]
        return node ? mapNode(node) : null
    })
}

export const RELATION_OPTIONS_REGISTRY = {
    Driver: {
        query: DriverOptionsQuery,
        getOptions: (data) => (data?.allDrivers?.edges || []).map(({ node }) => driverOption(node)),
        resolveById: (apolloClient, id) => resolveSingular(apolloClient, DriverByIdQuery, id, 'driver', driverOption),
    },
    Car: {
        query: CarOptionsQuery,
        getOptions: (data) => (data?.allCars?.edges || []).map(({ node }) => carOption(node)),
        resolveById: (apolloClient, id) => resolveSingular(apolloClient, CarByIdQuery, id, 'car', carOption),
    },
    User: {
        query: UserOptionsQuery,
        getOptions: (data) => (data?.allReps?.edges || []).map(({ node }) => userOption(node)),
        resolveById: (apolloClient, id) => resolveSingular(apolloClient, UserByIdQuery, id, 'rep', userOption),
    },
    Incident: {
        query: IncidentOptionsQuery,
        getOptions: (data) => (data?.allIncidents?.edges || []).map(({ node }) => incidentOption(node)),
        resolveById: (apolloClient, id) => resolveSingular(apolloClient, IncidentByIdQuery, id, 'incident', incidentOption),
    },
    CarIssues: {
        query: CarIssuesOptionsQuery,
        getOptions: (data) => (data?.allCarIssues?.edges || []).map(({ node }) => carIssueOption(node)),
        resolveById: (apolloClient, id) => resolveSingular(apolloClient, CarIssueByIdQuery, id, 'carIssue', carIssueOption),
    },
    LiabilityClaim: {
        query: LiabilityClaimOptionsQuery,
        getOptions: (data) => (data?.allLiabilityClaims?.edges || []).map(({ node }) => liabilityClaimOption(node)),
        resolveById: (apolloClient, id) => resolveSingular(apolloClient, LiabilityClaimByIdQuery, id, 'liabilityClaim', liabilityClaimOption),
    },
    SubrogationClaim: {
        query: SubrogationClaimOptionsQuery,
        getOptions: (data) => (data?.allSubrogationClaims?.edges || []).map(({ node }) => subrogationClaimOption(node)),
        resolveById: (apolloClient, id) => resolveSingular(apolloClient, SubrogationClaimByIdQuery, id, 'subrogationClaim', subrogationClaimOption),
    },
    Group: {
        query: GroupOptionsQuery,
        getOptions: (data) => (data?.allGroups?.edges || []).map(({ node }) => groupOption(node)),
        resolveById: (apolloClient, id) => resolveSingular(apolloClient, GroupByIdQuery, id, 'group', groupOption),
    },
    Branch: {
        query: BranchOptionsQuery,
        getOptions: (data) => (data?.allBranches?.edges || []).map(({ node }) => branchOption(node)),
        resolveById: (apolloClient, id) => resolveSingular(apolloClient, BranchByIdQuery, id, 'branch', branchOption),
    },
}
